import { DateTime } from "luxon"

import { parseDateTimeFromApi } from "../../../../../lib/time"
import { getOrderPerson } from "../../../../../shared/utils/getOrderPerson"

import { MeetingServiceOrder, MeetingServiceOrderStatus, MeetingServiceOrdersView } from "./meetingServiceTypes"

/**
 * Free-text search predicate for a meeting service order, matching on id, meeting name, delivery
 * location and the responsible person. Shared between the meeting-service table (`globalFilterFn`)
 * and the production view so both behave identically.
 */
export function meetingServiceOrderMatchesSearch(order: MeetingServiceOrder, search: string): boolean {
    if (!search) return true

    const needle = search.toLowerCase()
    const { responsible } = getOrderPerson(order.organizers)

    const id = String(order.id).toLowerCase()
    const meetingName = order.meetingDetails?.meetingName?.toLowerCase() ?? ""
    const location = order.deliveryDetails?.location?.name?.toLowerCase() ?? ""
    const responsibleName = responsible?.name?.toLowerCase() ?? ""

    return (
        id.includes(needle) ||
        meetingName.includes(needle) ||
        location.includes(needle) ||
        responsibleName.includes(needle)
    )
}

/**
 * Statuses that should NOT be offered in the status-change dropdown for a given view. Shared between
 * the meeting-service table and the production view so both expose precisely the same statuses — any
 * change here is reflected in both places.
 */
export function getHiddenOrderStatuses(view: MeetingServiceOrdersView): MeetingServiceOrderStatus[] {
    switch (view) {
        case MeetingServiceOrdersView.NOT_APPROVED:
            return [
                MeetingServiceOrderStatus.NOT_APPROVED,
                MeetingServiceOrderStatus.REJECTED,
                MeetingServiceOrderStatus.PREPARING,
                MeetingServiceOrderStatus.DELIVERED,
                MeetingServiceOrderStatus.TO_BE_BOOKED,
                MeetingServiceOrderStatus.BOOKED,
                MeetingServiceOrderStatus.READY,
                MeetingServiceOrderStatus.COMPLETED,
            ]
        case MeetingServiceOrdersView.TO_BE_BOOKED:
            return [
                MeetingServiceOrderStatus.NOT_APPROVED,
                MeetingServiceOrderStatus.APPROVED,
                MeetingServiceOrderStatus.REJECTED,
                MeetingServiceOrderStatus.DELIVERED,
                MeetingServiceOrderStatus.PREPARING,
                MeetingServiceOrderStatus.READY,
                MeetingServiceOrderStatus.COMPLETED,
            ]
        case MeetingServiceOrdersView.PRODUCTION:
            return [
                MeetingServiceOrderStatus.APPROVED,
                MeetingServiceOrderStatus.REJECTED,
                MeetingServiceOrderStatus.NOT_APPROVED,
                MeetingServiceOrderStatus.TO_BE_BOOKED,
                MeetingServiceOrderStatus.PREPARING,
                MeetingServiceOrderStatus.READY,
                MeetingServiceOrderStatus.COMPLETED,
            ]

        default:
            return []
    }
}

export type MeetingServiceClientSideFilters = Readonly<{
    pendingApproval?: boolean
    isBooked?: string
    startDeliveryDate?: DateTime | null
    endDeliveryDate?: DateTime | null
    email?: string
    employeeId?: string
}>

// The accounting and production endpoints return slightly different order shapes; only these fields are read.
type FilterableMeetingServiceOrder = Readonly<{
    accountingDetails?: Readonly<{ isBooked?: boolean }>
    deliveryDetails: Readonly<{ time: string; statusDetails?: Readonly<{ status: string }> }>
    customer?: Readonly<{ email?: string; employeeId?: string }>
}>

const BOOKED_FILTER = { BOOKED: "BOOKED", NOT_BOOKED: "NOT_BOOKED" } as const

function matchesBooked(order: FilterableMeetingServiceOrder, isBooked: string | undefined) {
    if (isBooked === BOOKED_FILTER.BOOKED) return order.accountingDetails?.isBooked === true
    if (isBooked === BOOKED_FILTER.NOT_BOOKED) return !order.accountingDetails?.isBooked
    return true
}

function matchesDeliveryDate(
    order: FilterableMeetingServiceOrder,
    start: DateTime | null | undefined,
    end: DateTime | null | undefined,
) {
    if (!start && !end) return true
    const time = parseDateTimeFromApi(order.deliveryDetails.time)
    if (start && time < start.startOf("day")) return false
    if (end && time > end.endOf("day")) return false
    return true
}

function matchesText(value: string | undefined, filter: string | undefined) {
    const needle = filter?.trim().toLowerCase()
    return !needle || (value ?? "").toLowerCase().includes(needle)
}

/**
 * The meeting service endpoints do not apply these drawer filters yet, so the loaded orders are
 * narrowed here until the backend implements them (orderId is left to the backend on purpose).
 */
export function filterMeetingServiceOrders<TOrder extends FilterableMeetingServiceOrder>(
    orders: ReadonlyArray<TOrder>,
    filters: MeetingServiceClientSideFilters,
): TOrder[] {
    return orders.filter(
        (order) =>
            (!filters.pendingApproval ||
                order.deliveryDetails.statusDetails?.status === MeetingServiceOrderStatus.NOT_APPROVED) &&
            matchesBooked(order, filters.isBooked) &&
            matchesDeliveryDate(order, filters.startDeliveryDate, filters.endDeliveryDate) &&
            matchesText(order.customer?.email, filters.email) &&
            matchesText(order.customer?.employeeId, filters.employeeId),
    )
}
